/**
 * Lightweight client-side error reporter — ships unhandled errors to /api/client-errors.
 *
 * Wired up in src/index.js. Public ingest endpoint accepts unauthenticated payloads,
 * so this works pre-login. Includes in-memory dedup to avoid spamming on render loops.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;
const APP_VERSION = process.env.REACT_APP_VERSION || 'dev';

// Same-signature throttle: 1 report per minute per error signature
const seen = new Map(); // signature -> last timestamp
const COOLDOWN_MS = 60_000;
let installed = false;

function signatureOf(message, stack) {
  const first = (stack || '').split('\n').slice(0, 3).join('|');
  return `${message || 'unknown'}::${first}`.slice(0, 400);
}

function tooSoon(sig) {
  const last = seen.get(sig);
  const now = Date.now();
  if (last && now - last < COOLDOWN_MS) return true;
  seen.set(sig, now);
  // Keep the map bounded
  if (seen.size > 200) {
    const oldest = [...seen.entries()].sort((a, b) => a[1] - b[1])[0];
    if (oldest) seen.delete(oldest[0]);
  }
  return false;
}

export async function reportError(payload) {
  try {
    if (!API_URL) return;
    const message = (payload?.message || '').toString().slice(0, 2000);
    if (!message) return;
    const sig = signatureOf(message, payload?.stack);
    if (tooSoon(sig)) return;

    const token = (() => { try { return localStorage.getItem('token'); } catch { return null; } })();
    const body = {
      message,
      stack: (payload?.stack || '').toString().slice(0, 20000),
      source: payload?.source || null,
      lineno: payload?.lineno ?? null,
      colno: payload?.colno ?? null,
      url: typeof window !== 'undefined' ? window.location.href : null,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      type: payload?.type || 'error',
      component_stack: payload?.componentStack || null,
      app_version: APP_VERSION,
      extra: payload?.extra || null,
    };

    // Use sendBeacon if available (works during page-unload too)
    if (typeof navigator !== 'undefined' && navigator.sendBeacon && !token) {
      try {
        const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
        const ok = navigator.sendBeacon(`${API_URL}/api/client-errors`, blob);
        if (ok) return;
      } catch { /* fall through to fetch */ }
    }

    await fetch(`${API_URL}/api/client-errors`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch {
    // Last-resort silent — never let the reporter throw
  }
}

export function installErrorReporter() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    // Some browsers fire a `error` event with no error object (e.g., cross-origin script errors)
    const err = event?.error;
    reportError({
      message: err?.message || event?.message || 'Unknown error',
      stack: err?.stack,
      source: event?.filename,
      lineno: event?.lineno,
      colno: event?.colno,
      type: 'error',
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    const message = typeof reason === 'string' ? reason : reason?.message || 'Unhandled Promise rejection';
    const stack = typeof reason === 'object' ? reason?.stack : undefined;
    reportError({ message, stack, type: 'unhandledrejection' });
  });
}
